import * as React from 'react';
import { Check, Loader2, MapPin, Search } from 'lucide-react';
import { type GeocodeSuggestion } from '@hch/shared';
import { Badge, Field, Input, Separator } from '~/components/ui/primitives';
import { useAddresses, useGeocodeSearch, useZoneResolution } from '~/lib/queries';
import { useAuth } from '~/lib/auth';
import { cn } from '~/lib/utils';
import type { BookingDraft } from '../booking-types';
import { StepShell } from '../step-shell';
import { OutOfCoverage } from './waiting-list-form';

/**
 * Tunnel de reservation — parcours principal de l'application.
 *
 * L'ordre des etapes suit litteralement l'enonce : adresse, cycle, operation de
 * maintenance, forfait, creneau, complements. Deux partis pris :
 *
 *   - La couverture est verifiee DES l'etape 1. Un client hors zone l'apprend
 *     immediatement, avant d'avoir saisi quoi que ce soit d'autre — plutot qu'apres
 *     cinq etapes, ce qui serait une perte de temps et une mauvaise experience.
 *
 *   - Le compte n'est jamais un prealable (RG-04). Un visiteur connecte se voit
 *     simplement proposer ses adresses et ses cycles enregistres.
 */

export function AddressStep({
  draft,
  patch,
  onNext,
}: {
  draft: BookingDraft;
  patch: (values: Partial<BookingDraft>) => void;
  onNext: () => void;
}) {
  const { isAuthenticated } = useAuth();
  const { data: savedAddresses } = useAddresses(isAuthenticated);

  // Le champ repart du libelle DEJA retenu, et non d'une chaine vide.
  //
  // Le defaut : `query` etait un etat local initialise a '', vide a chaque montage. Au
  // retour a l'etape — bouton « Retour », ou rechargement qui restitue le brouillon —
  // l'adresse choisie s'affichait dans l'encart du dessous mais le champ de recherche
  // restait blanc. On croyait avoir perdu sa saisie, et l'on retapait par-dessus.
  //
  // La lecture du brouillon est synchrone (voir useEtatPersistant) : le libelle est deja
  // la au premier rendu, l'initialisation paresseuse suffit.
  const [query, setQuery] = React.useState(() => draft.addressLabel ?? '');
  const [debounced, setDebounced] = React.useState('');

  // Antirebond : la BAN est interrogee a l'arret de la frappe, pas a chaque touche.
  React.useEffect(() => {
    // Mais JAMAIS sur l'adresse deja retenue : sinon le simple fait de revenir a
    // l'etape rouvrait une liste de suggestions sous un champ que personne n'a touche,
    // et consommait un appel a la BAN pour un resultat deja connu.
    if (query === draft.addressLabel) {
      setDebounced('');
      return;
    }
    const timer = setTimeout(() => setDebounced(query), 350);
    return () => clearTimeout(timer);
  }, [query, draft.addressLabel]);

  const { data: suggestions, isFetching, error } = useGeocodeSearch(debounced);

  const coords = draft.newAddress ? { lat: draft.newAddress.lat, lng: draft.newAddress.lng } : null;
  const { data: coverage, isLoading: checkingCoverage } = useZoneResolution(coords);

  function pick(suggestion: GeocodeSuggestion) {
    patch({
      addressId: undefined,
      addressLabel: suggestion.label,
      newAddress: {
        street: suggestion.street,
        city: suggestion.city,
        postalCode: suggestion.postalCode,
        country: suggestion.country,
        lat: suggestion.lat,
        lng: suggestion.lng,
        isDefault: false,
      },
    });
    // Le champ garde l'adresse choisie : c'est l'accuse de reception de la selection.
    // La liste, elle, se referme — `debounced` vide desactive la requete.
    setQuery(suggestion.label);
    setDebounced('');
  }

  const covered = coverage?.covered === true;

  /** Repart d'une recherche vierge : utilise quand l'adresse s'avere hors secteur. */
  function reprendreRecherche() {
    patch({ addressId: undefined, newAddress: undefined, addressLabel: undefined });
    setQuery('');
    setDebounced('');
  }

  return (
    <StepShell
      title="Ou intervenons-nous ?"
      // Dit explicitement qu'aucun compte n'est demande (RG-04) : c'est la question
      // que se pose le visiteur en arrivant sur « Prendre rendez-vous » sans etre
      // connecte, et ne pas y repondre le fait hesiter des la premiere etape.
      description="Aucun compte n'est necessaire pour reserver. Nous verifions immediatement que votre secteur est desservi."
      onNext={onNext}
      nextDisabled={!covered}
    >
      {/* Adresses enregistrees : l'enonce demande que l'adresse du client connecte
          lui soit suggeree. */}
      {isAuthenticated && savedAddresses && savedAddresses.length > 0 ? (
        <div className="space-y-2">
          <p className="app-section-title">Mes adresses</p>
          <div className="flex flex-col gap-2">
            {savedAddresses.map((address) => (
              <button
                key={address.id}
                type="button"
                onClick={() => {
                  const libelle = `${address.street}, ${address.postalCode} ${address.city}`;
                  patch({ addressId: address.id, newAddress: undefined, addressLabel: libelle });
                  // Meme regle que pour une suggestion : le champ porte le choix, et la
                  // liste se referme. Sans cela, le texte deja tape restait dans le
                  // champ et relancait une recherche a cote de l'adresse retenue.
                  setQuery(libelle);
                  setDebounced('');
                }}
                className={cn(
                  'flex items-start gap-3 rounded-lg border p-3 text-left transition-colors hover:bg-accent',
                  draft.addressId === address.id ? 'border-primary bg-accent' : 'border-border',
                )}
              >
                <MapPin className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{address.street}</span>
                  <span className="block text-xs text-muted-foreground">
                    {address.postalCode} {address.city}
                  </span>
                </span>
                {address.isDefault ? <Badge variant="secondary">Par defaut</Badge> : null}
              </button>
            ))}
          </div>
          <Separator className="my-2" />
        </div>
      ) : null}

      {/* Forme fonction : le controle est enveloppe par un conteneur positionne,
          les attributs doivent donc atterrir sur l'`Input` et non sur le `div`. */}
      <Field
        label="Rechercher une adresse"
        htmlFor="adresse"
        hint="Saisissez au moins 4 caracteres, puis choisissez dans la liste."
        required
      >
        {(control) => (
          <div className="relative">
            <Search
              className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            />
            <Input
              {...control}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="12 rue de la Republique, Lyon"
              className="pl-9"
              autoComplete="off"
              role="combobox"
              aria-expanded={!!suggestions?.length}
              aria-autocomplete="list"
            />
            {isFetching ? (
              <Loader2
                className="absolute top-1/2 right-3 size-4 -translate-y-1/2 animate-spin text-muted-foreground"
                aria-hidden
              />
            ) : null}
          </div>
        )}
      </Field>

      {error ? (
        <p role="alert" className="text-sm text-destructive">
          Le service de validation d'adresse est momentanement indisponible. Reessayez dans quelques
          instants.
        </p>
      ) : null}

      {suggestions && suggestions.length > 0 ? (
        <ul className="flex flex-col gap-1 rounded-lg border border-border p-1">
          {suggestions.map((suggestion) => (
            <li key={`${suggestion.label}-${suggestion.lat}`}>
              <button
                type="button"
                onClick={() => pick(suggestion)}
                className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm hover:bg-accent"
              >
                <MapPin className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                <span className="truncate">{suggestion.label}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {draft.addressLabel ? (
        <div className="app-summary-box space-y-2">
          <p className="text-sm font-medium">{draft.addressLabel}</p>

          {checkingCoverage ? (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" aria-hidden />
              Verification de la couverture…
            </p>
          ) : null}

          {coverage?.covered ? (
            <p className="flex items-center gap-2 text-sm font-medium text-success">
              <Check className="size-4" aria-hidden />
              Secteur couvert — {coverage.zone.name} ({coverage.technicianCount} technicien
              {coverage.technicianCount > 1 ? 's' : ''})
            </p>
          ) : null}

          {coverage && !coverage.covered ? (
            <OutOfCoverage
              message={coverage.message}
              address={draft.newAddress}
              onRetry={reprendreRecherche}
            />
          ) : null}

          {/* Adresse enregistree du compte : la couverture a deja ete validee lors
              de son enregistrement, on ne rejoue pas la verification. */}
          {draft.addressId && !coverage ? (
            <p className="flex items-center gap-2 text-sm font-medium text-success">
              <Check className="size-4" aria-hidden />
              Adresse enregistree
            </p>
          ) : null}
        </div>
      ) : null}
    </StepShell>
  );
}

/**
 * RG-05 : le prospect hors zone est invite a laisser ses coordonnees.
 *
 * Les donnees sont reellement envoyees a l'API et enregistrees en base ; elles
 * alimentent l'ecran « Liste d'attente » du back-office. Annoncer un enregistrement
 * sans l'effectuer serait un mensonge fait a l'utilisateur.
 */
